import React, { useEffect, useState, useCallback } from "react";
import Loader from "../../util/preloader/Preloader";
import { useNavigate, useParams } from "react-router-dom";

import { faCheck, faFaceFrown, faFaceMeh, faFaceSmile } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import swal from "sweetalert";
import { ENV } from "../../util/Env";

const SelfAttendance = () => {
  const { eventId, userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ latitude: null, longitude: null });
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [locationStatus, setLocationStatus] = useState('requesting');
  const [permissionState, setPermissionState] = useState('prompt'); // 'granted', 'denied', 'prompt'

  const markAttendanceAPI = useCallback(async (lat, lng) => {
    setLoading(true);
    try {
      const response = await fetch(ENV.API_URL + 'web/api/v1/lms-web/website/participantSelfAttendance/' + eventId + '/' + userId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // latitude: lat,
          // longitude: lng,

          // Switched lat and lng as per requirement
          latitude: lng,
          longitude: lat,
        }),
      });

      const result = await response.json();

      if (response.ok && result) {
        setAttendanceMarked(true);
        swal({
          title: "Attendance Marked!",
          text: "Your attendance has been successfully captured and recorded.",
          icon: "success",
          button: "OK",
          closeOnClickOutside: false,
          closeOnEsc: false, 
        }).then((value) => {
        if (value === true) {
          navigate(`/`);
        }
      });
      } else {
        throw new Error(result.message || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Attendance marking error:', error);
      swal({
        title: error.message === "Your attendance is already marked for this event."
          ? ""
          : "Error",
        text: `${error.message}`,
        icon: error.message === "Your attendance is already marked for this event."
          ? "warning"
          : "error",
        buttons: {
          retry: {
            text: "Retry",
            value: "retry",
            visible: error.message === "Your attendance is already marked for this event."
              ? false
              : true,
            className: "btn-primary"
          },
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            className: "btn-secondary"
          }
        },
        closeOnClickOutside: false,
        closeOnEsc: false,
      }).then((result) => {
        if (result === "retry") {
          markAttendanceAPI(lat, lng);
        }
      });
    } finally {
      setLoading(false);
    }
  }, [userId, eventId]);

  // Check current permission status
  const checkPermissionStatus = useCallback(async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionState(result.state);
        console.log('Permission state:', result.state);

        // Listen for permission changes
        result.onchange = () => {
          setPermissionState(result.state);
          console.log('Permission changed to:', result.state);
          if (result.state === 'granted') {
            // Permission granted, try to get location again
            setTimeout(() => {
              window.location.reload(); // Refresh to restart the flow
            }, 500);
          }
        };
      } catch (error) {
        console.log('Permissions API not available:', error);
      }
    }
  }, []);

  const showLocationErrorPopup = useCallback((message) => {
    swal({
      title: "Location Access Required",
      text: `${message}\n\nTo enable location access:\n1. Click the location/lock icon in your browser's address bar\n2. Select 'Allow' for location\n3. Click "Refresh Page" below`,
      icon: "warning",
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: "btn-cancel"
        },
        confirm: {
          text: "Refresh Page",
          value: true,
          visible: true,
          className: "btn-primary"
        }
      },
      dangerMode: false,
      closeOnClickOutside: false,
      closeOnEsc: false,
    }).then((result) => {
      if (result === true) {
        // User wants to refresh after enabling location
        window.location.reload();
      } else {
        // User clicked "Cancel"
        setLocationStatus('denied');
        swal({
          title: "Attendance Cannot Be Marked",
          text: "Without location access, attendance cannot be marked. Please enable location access to mark your attendance.",
          icon: "error",
          button: "OK",
        });
      }
    });
  }, []);

  const requestLocationWithTimeout = useCallback(() => {
    setLocationStatus('requesting');

    const timeoutId = setTimeout(() => {
      setLocationStatus('denied');
      showLocationErrorPopup("Location request timed out. Please enable location access manually.");
    }, 10000);

    if (!navigator.geolocation) {
      clearTimeout(timeoutId);
      showLocationErrorPopup("Geolocation is not supported by this browser.");
      setLocationStatus('denied');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      function (position) {
        clearTimeout(timeoutId);
        console.log("Location obtained:", position);
        setLocationStatus('granted');
        setPosition({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        markAttendanceAPI(position.coords.latitude, position.coords.longitude);
      },
      function (error) {
        clearTimeout(timeoutId);
        console.log("Geolocation error:", error);
        setLocationStatus('denied');

        let errorMessage = "";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access was denied. Please enable location access to mark attendance.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable. Please try again.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorMessage = "An unknown error occurred while retrieving location.";
            break;
        }

        showLocationErrorPopup(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }, [markAttendanceAPI, showLocationErrorPopup]);

  const getLocationPermission = useCallback(() => {
    requestLocationWithTimeout();
  }, [requestLocationWithTimeout]);

  useEffect(() => {
    checkPermissionStatus();
    getLocationPermission();
  }, [checkPermissionStatus, getLocationPermission]);


  return (
    <React.Fragment>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* main page  */}
          <div className="usp_main Feedbackbg">
            <div className="container-lg py-4">
              <div className="self_attendance_main">
                <h5>Self Attendance</h5>

                {/* Instructions Section */}
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title">Instructions</h5>
                        <ul className="list-unstyled">
                          <li className="mb-2">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                            Click "Enable Location" when prompted to allow location access
                          </li>
                          <li className="mb-2">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                            Attendance will be marked automatically once location is obtained
                          </li>
                          <li className="mb-2">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                            Ensure you are at the correct location for the event
                          </li>
                          <li className="mb-2">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                            If location access is blocked, please "Enable Location" permission in your browser settings
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export default SelfAttendance;
